import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { useOnboarding } from '../../../contexts/onboarding.context';
import OnbFrame from '../_shared/OnbFrame';
import { COLORS } from '../_shared/tokens';
import useAppInfo from '../../../hooks/appInfo.hook';
import { onboardingSteps } from '../../../utils/constants';

const NODES = (isWebview) => {
    if (isWebview) {
        return [
            { Icon: QrCodeScannerIcon, title: 'Baixe o Visualizador', body: 'para ver o app no seu celular' },
            {
                Icon: NotificationsActiveOutlinedIcon,
                title: `Envie uma notificação`,
                body: 'do mesmo jeito que você vai enviar para os seus clientes',
                reward: true
            }
        ];
    }
    return [
        { Icon: PaletteOutlinedIcon, title: 'Personalize seu app', body: 'logo, banner e cor', now: true },
        { Icon: QrCodeScannerIcon, title: 'Baixe o Visualizador', body: 'para ver o app no seu celular' },
        {
            Icon: NotificationsActiveOutlinedIcon,
            title: `Envie uma notificação`,
            body: 'do mesmo jeito que você vai enviar para os seus clientes',
            reward: true
        }
    ];
}

function TimelineNode({ node }) {
    const { Icon, title, body, now, reward } = node;

    return (
        <Box
            sx={{
                width: { xs: '100%', md: '31%' },
                display: 'flex',
                flexDirection: { xs: 'row', md: 'column' },
                alignItems: { xs: 'flex-start', md: 'center' },
                gap: { xs: 2, md: 0 },
                textAlign: { xs: 'left', md: 'center' },
                position: 'relative'
            }}
        >
            {reward && (
                <Box
                    aria-hidden
                    sx={{
                        display: { xs: 'none', md: 'block' },
                        position: 'absolute',
                        top: -30,
                        width: 150,
                        height: 150,
                        background: 'radial-gradient(circle, rgba(60,213,104,0.22), transparent 70%)',
                        borderRadius: '50%'
                    }}
                />
            )}

            <Box
                sx={{
                    width: { xs: 56, md: 78 },
                    height: { xs: 56, md: 78 },
                    borderRadius: '50%',
                    flex: 'none',
                    position: 'relative',
                    backgroundColor: reward ? COLORS.forest : now ? COLORS.green : '#fff',
                    border: reward || now ? 0 : `2px solid ${COLORS.border}`,
                    color: reward || now ? '#fff' : COLORS.textMuted,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: now
                        ? '0 6px 18px rgba(60,213,104,0.35)'
                        : reward
                            ? '0 6px 18px rgba(10,77,32,0.22)'
                            : 'none'
                }}
            >
                <Icon sx={{ fontSize: { xs: 26, md: 34 } }} />
            </Box>

            <Box>
                <Box
                    sx={{
                        mt: { xs: 0, md: 2.25 },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: { xs: 'flex-start', md: 'center' },
                        gap: 1,
                        fontWeight: 700,
                        fontSize: { xs: 16, md: 17 },
                        lineHeight: 1.2,
                        color: reward ? COLORS.forest : '#000'
                    }}
                >
                    {title}
                    {now && (
                        <Box
                            component="span"
                            sx={{
                                fontWeight: 700,
                                fontSize: 11,
                                px: 1,
                                py: 0.375,
                                borderRadius: '999px',
                                backgroundColor: 'rgba(60,213,104,0.14)',
                                color: COLORS.forest,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}
                        >
                            agora
                        </Box>
                    )}
                </Box>

                <Typography sx={{ mt: 0.75, fontSize: 15, lineHeight: 1.45, color: COLORS.textMuted, maxWidth: 240 }}>
                    {body}
                </Typography>
            </Box>
        </Box>
    );
}

/**
 * D01 · A promessa (step 3 of 7) — the store is connected; sells the two steps
 * left and the reward. Desktop lays the timeline horizontally, mobile stacks it.
 */
export default function Promise() {
    const { goToNextStep, goToStep, data } = useOnboarding();
    const { appInfo } = useAppInfo()
    const { platformId } = appInfo
    const isWebview = platformId === 'webview' || platformId === 'lojaintegrada';

    const nextPage = () => {
        if (isWebview) {
            goToStep(onboardingSteps.PREVIEW)
            return
        }

        goToNextStep()
    }

    return (
        <OnbFrame step={3}>
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: { xs: 'flex-start', md: 'center' },
                    textAlign: { xs: 'left', md: 'center' }
                }}
            >
                <Box
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1.25,
                        px: 1.75,
                        py: 1,
                        borderRadius: '999px',
                        backgroundColor: COLORS.successBg,
                        color: COLORS.successFg,
                        fontWeight: 700,
                        fontSize: 14,
                        mb: 3
                    }}
                >
                    <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
                    {data.storeName} conectada
                </Box>

                <Typography
                    component="h1"
                    sx={{ fontWeight: 700, fontSize: { xs: 28, md: 46 }, lineHeight: 1.1, letterSpacing: '-1px', maxWidth: 760, mb: 2.25 }}
                >
                    O app da {data.storeName} está{' '}
                    <Box component="span" sx={{ color: COLORS.green }}>quase pronto!</Box>
                </Typography>

                <Typography sx={{ fontSize: { xs: 16, md: 18 }, lineHeight: 1.5, color: COLORS.textMuted, maxWidth: 620, mb: { xs: 4, md: 7 } }}>
                    Você está a pouco passos de ver o aplicativo da sua loja e testar como ele funciona no seu próprio celular.
                </Typography>

                {/* Timeline: horizontal rail on desktop, stacked on mobile */}
                <Box sx={{ position: 'relative', width: '100%', maxWidth: isWebview ? 800 : 940, mb: { xs: 4, md: 6.5 } }}>
                    <Box
                        aria-hidden
                        sx={{
                            display: { xs: 'none', md: 'block' },
                            position: 'absolute',
                            top: 39,
                            left: '16.6%',
                            right: '16.6%',
                            height: 2,
                            backgroundColor: COLORS.divider
                        }}
                    />

                    <Box
                        sx={{
                            position: 'relative',
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            justifyContent: 'space-between',
                            gap: { xs: 3, md: 0 }
                        }}
                    >
                        {NODES(isWebview).map((node) => (
                            <TimelineNode key={node.title} node={node} />
                        ))}
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: { xs: 'stretch', md: 'center' },
                        gap: 1.75,
                        width: { xs: '100%', md: 'auto' }
                    }}
                >
                    <Button variant="contained" onClick={nextPage} endIcon={<ArrowForwardIcon />} sx={{ py: 2, fontSize: 16 }}>
                        continuar
                    </Button>

                    <Typography sx={{ fontSize: 14, color: COLORS.textMuted, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <ScheduleIcon sx={{ fontSize: 16 }} />
                        leva menos de 3 minutos
                    </Typography>
                </Box>
            </Box>
        </OnbFrame>
    );
}
